import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users, Plus, Loader2, MessageSquare, Crown } from 'lucide-react';
import { toast } from 'sonner';

export default function GroupsPage() {
  const { user, token, authHeaders } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchGroups(); }, []);

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/groups`);
      setGroups(res.data.groups);
    } catch {}
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Nazwa grupy wymagana'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/groups`, { name: newName, description: newDesc }, { headers: authHeaders() });
      toast.success('Grupa utworzona! +15 Zorków');
      setNewName(''); setNewDesc(''); setDialogOpen(false);
      fetchGroups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Błąd'); }
    setCreating(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="groups-page">
      <SEO title="Grupy" description="Społeczności łowców okazji. Dołącz do grup tematycznych!" />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" /> GRUPY
          </h1>
          <p className="text-sm text-muted-foreground">Społeczności łowców okazji</p>
        </div>
        {token && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground rounded-none font-heading uppercase tracking-wider text-xs" data-testid="create-group-btn">
                <Plus className="w-4 h-4 mr-1" /> Nowa grupa
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading text-white">Utwórz nową grupę</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-sm text-muted-foreground mb-1 block">Nazwa grupy *</Label>
                  <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Np. Łowcy elektroniki" className="bg-input border-transparent focus:border-primary rounded-none" data-testid="group-name-input" />
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground mb-1 block">Opis</Label>
                  <Textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="O czym jest ta grupa?" rows={3} className="bg-input border-transparent focus:border-primary rounded-none resize-none" data-testid="group-desc-input" />
                </div>
                <Button onClick={handleCreate} disabled={creating} className="w-full bg-primary text-primary-foreground rounded-none font-heading uppercase" data-testid="submit-group-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Utwórz grupę (+15 Zorków)'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
      ) : groups.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">Brak grup. Utwórz pierwszą!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="groups-list">
          {groups.map(g => {
            const cover = g.cover_image_local ? `${BACKEND}${g.cover_image_local}` : g.cover_image_url;
            return (
              <Link key={g.id} to={`/groups/${g.id}`} className="bg-card border border-border/40 hover:border-primary/30 transition-colors group overflow-hidden" data-testid={`group-card-${g.id}`}>
                {cover ? (
                  <div className="h-24 bg-secondary overflow-hidden">
                    <img src={cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  </div>
                ) : (
                  <div className="h-24 bg-secondary flex items-center justify-center">
                    <Users className="w-8 h-8 text-muted-foreground/20" />
                  </div>
                )}
                <div className="p-3">
                  <h3 className="font-heading font-bold text-white group-hover:text-primary transition-colors">{g.name}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{g.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {g.members_count}</span>
                    <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {g.posts_count}</span>
                    <span className="flex items-center gap-1"><Crown className="w-3 h-3 text-zorki" /> {g.owner_nick}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
